/**
 * Paints a World onto the canvas. Far to near. Everything on the water goes through
 * `project` so it lands exactly where the engine thinks it is.
 */
import { FACES, TUNING, bobHeight, gradUp, snorkelerUp, turtleUp, type ThrowKind, type Who, type World } from './engine.ts';
import type { Aim } from './input.ts';
import { project, type View } from './project.ts';
import { cap as capSprite, catamaran as catamaranSprite, coconut as coconutSprite, fish as fishSprite, fromImage, grad as gradSprite, log as logSprite, pelican as pelicanSprite, pineapple as pineappleSprite, rock as rockSprite, sharkFin as sharkSprite, shoe as shoeSprite, snorkeler as snorkelerSprite, turtle as turtleSprite, type Sprite } from './sprites.ts';

/** public/bg/horizon.jpg: the horizon row and the last pure-sky row, as fractions of its height */
const BG_HORIZON = 145 / 210;
const BG_SKY = 92 / 210;

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLImageElement | null = null;
  /** the colour of the photo's top rows, for the sky above it */
  private skyTop = '#b9d6e6';
  /** real coconuts, photographed on the trip; a coconut's id picks which */
  private cocos: Sprite[];
  private turt: Sprite;
  private boat: Sprite;
  private snork: Sprite;
  private shark: Sprite;
  private bird: Sprite;
  private log: Sprite;
  private grad: Sprite;
  private cap: Sprite;
  /** the capuchin, a photo cut-out that rides the log */
  private monkey: HTMLImageElement;
  private sprites: Record<ThrowKind, Sprite>;
  /** the Feed round's faces: head and jaw pictures per person, loaded on first use */
  private faceImgs: Partial<Record<Who, { head: HTMLImageElement; jaw: HTMLImageElement }>> = {};
  private dpr = 1;
  /** the gun's screen position (bottom centre, before sway) */
  private gunX = 0;
  private gunY = 0;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d', { alpha: false })!;
    const drawn = coconutSprite();
    this.cocos = [fromImage('/sprites/coconut-1.png', 0.34, 0.6, drawn), fromImage('/sprites/coconut-2.png', 0.34, 0.6, drawn)];
    this.turt = turtleSprite();
    this.boat = catamaranSprite();
    this.snork = snorkelerSprite();
    this.shark = sharkSprite();
    this.bird = pelicanSprite();
    this.log = logSprite();
    this.grad = gradSprite();
    this.cap = capSprite();
    this.monkey = new Image();
    this.monkey.src = '/sprites/monkey.webp';
    const pine = pineappleSprite();
    this.sprites = { rock: rockSprite(), pineapple: pine, fish: fishSprite(), shoe: shoeSprite(), super: pine };
    const img = new Image();
    img.src = '/bg/horizon.jpg';
    img.onload = () => {
      this.bg = img;
      const c = document.createElement('canvas');
      c.width = 1;
      c.height = 1;
      const x = c.getContext('2d')!;
      x.drawImage(img, 0, 0, img.width, 6, 0, 0, 1, 1);
      const [r, g, b] = x.getImageData(0, 0, 1, 1).data;
      this.skyTop = `rgb(${r},${g},${b})`;
    };
  }

  /** `insetBottom`: css px hidden under a system bar at the bottom, so the arm and rail sit above it */
  resize(w: number, h: number, insetBottom = 0): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.gunX = w / 2;
    this.gunY = h - insetBottom;
  }

  /** load a face's two layers (idempotent) */
  loadFace(who: Who): void {
    if (this.faceImgs[who]) return;
    const head = new Image();
    head.src = `/faces/${who}-head.webp`;
    const jaw = new Image();
    jaw.src = `/faces/${who}-jaw.webp`;
    this.faceImgs[who] = { head, jaw };
  }

  draw(w: World, v: View, aim: Aim | null): void {
    const { ctx } = this;
    const { w: W, h: H } = v;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // --- the world, under sway ------------------------------------------------
    ctx.save();
    ctx.translate(W / 2, H / 2);
    ctx.rotate(v.roll);
    ctx.translate(-W / 2, -H / 2 + v.heave);
    this.backdrop(v);
    this.water(w, v);
    ctx.restore();

    // --- things on the water (project() applies the sway itself) ---------------
    this.things(w, v);

    // --- the thrower, fixed to the boat --------------------------------------------
    this.gun(w, v, aim);
  }

  private backdrop(v: View): void {
    const { ctx } = this;
    const { w: W, h: H } = v;
    // wide enough that the roll never shows an edge
    const bw = W * 1.3;
    const bx = (W - bw) / 2;
    ctx.fillStyle = this.skyTop;
    ctx.fillRect(bx, -H, bw, v.horizonY + H + 1);
    if (!this.bg) return;
    const iw = this.bg.width;
    const ih = this.bg.height;
    const k = bw / iw;
    // land and sea rows at their natural aspect, horizon on horizonY
    const skyRows = Math.round(ih * BG_SKY);
    const landTop = v.horizonY - (ih * BG_HORIZON - skyRows) * k;
    ctx.drawImage(this.bg, 0, skyRows, iw, ih - skyRows, bx, landTop, bw, (ih - skyRows) * k);
    // the sky rows, stretched a little (clouds forgive 1.5x) and the plain colour above that
    const skyH = Math.min(landTop + 0.3 * H, skyRows * k * 1.5);
    ctx.drawImage(this.bg, 0, 0, iw, skyRows, bx, landTop - skyH, bw, skyH + 1);
    const fade = ctx.createLinearGradient(0, landTop - skyH, 0, landTop - skyH + 40);
    fade.addColorStop(0, this.skyTop);
    fade.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = fade;
    ctx.fillRect(bx, landTop - skyH, bw, 40);
  }

  private water(w: World, v: View): void {
    const { ctx } = this;
    const { w: W, h: H } = v;
    const bw = W * 1.3;
    const bx = (W - bw) / 2;
    const top = v.horizonY;
    const g = ctx.createLinearGradient(0, top, 0, H);
    g.addColorStop(0, '#a7d2d0');
    g.addColorStop(0.12, '#6fb9b8');
    g.addColorStop(0.5, '#3f9c9a');
    g.addColorStop(1, '#2b7f80');
    ctx.fillStyle = g;
    ctx.fillRect(bx, top - 1, bw, H * 2);

    // swell bands at projected depths: they compress toward the horizon, which is the depth cue
    const t = w.t;
    ctx.lineCap = 'round';
    let d = 3.2;
    let k = 0;
    while (d < 160) {
      const dd = d * (1 + 0.025 * Math.sin(t * 0.85 + k * 0.9));
      const s = v.f / dd;
      const y = v.horizonY + s * v.eyeH; // unswayed, we are inside the swayed transform
      const alpha = Math.min(0.32, 0.05 + 1.6 / dd);
      ctx.strokeStyle = `rgba(230,250,248,${alpha.toFixed(3)})`;
      ctx.lineWidth = Math.max(0.6, Math.min(4, 14 / dd * (H / 800)));
      ctx.beginPath();
      const amp = Math.min(6, 40 / dd);
      const freq = 0.012 + 0.08 / dd;
      const drift = t * (0.6 + 4 / dd) * -w.current;
      for (let x = bx; x <= bx + bw; x += 10) {
        const yy = y + amp * Math.sin(x * freq + drift + k);
        if (x === bx) ctx.moveTo(x, yy);
        else ctx.lineTo(x, yy);
      }
      ctx.stroke();
      d *= 1.14;
      k++;
    }
  }

  private things(w: World, v: View): void {
    const { ctx } = this;
    type Item = { d: number; paint: () => void };
    const items: Item[] = [];
    const t = w.t;

    for (const b of w.boats) {
      items.push({
        d: b.d,
        paint: () => {
          const p = project(v, b.x, b.d, 0);
          const px = this.boat.metres * b.scale * p.s;
          ctx.save();
          ctx.translate(p.sx, p.sy);
          ctx.rotate(v.roll);
          ctx.scale(b.heading, 1);
          ctx.globalAlpha = 0.55 * haze(b.d) + 0.15;
          ctx.drawImage(this.boat.canvas, -px / 2, -this.boat.waterline * px, px, px);
          ctx.restore();
        },
      });
    }

    for (const c of w.coconuts) {
      items.push({
        d: c.d,
        paint: () => {
          const bob = bobHeight(t, c.phase, c.phase2);
          const sink = c.state === 'hit' ? Math.min(1, (t - c.hitAt) / TUNING.coconut.sinkTime) : 0;
          const sp = this.cocos[c.id % this.cocos.length];
          const p = project(v, c.x, c.d, bob + 0.06 - sink * 0.35);
          const px = sp.metres * p.s;
          // how much of it is under: more when in a trough, all of it as it sinks
          const under = 0.38 + 0.18 * Math.sin(t * 2.7 + c.phase) + sink * 0.6;
          this.sprite(sp, p.sx, p.sy, px, v.roll + 0.35 * Math.sin(c.tilt), Math.min(1, under), haze(c.d));
        },
      });
    }
    for (const tu of w.turtles) {
      const up = turtleUp(tu, t);
      if (up <= 0) continue;
      items.push({
        d: tu.d,
        paint: () => {
          const p = project(v, tu.x, tu.d, 0.05);
          const px = this.turt.metres * p.s;
          this.sprite(this.turt, p.sx, p.sy, px, v.roll, 1 - up * 0.62, haze(tu.d) * (0.4 + 0.6 * up));
        },
      });
    }
    for (const sn of w.snorkelers) {
      const up = snorkelerUp(sn, t);
      if (up <= 0) continue;
      items.push({
        d: sn.d,
        paint: () => {
          const p = project(v, sn.x, sn.d, 0.05 + 0.04 * Math.sin(t * 2.1 + sn.id));
          const px = this.snork.metres * p.s;
          this.sprite(this.snork, p.sx, p.sy, px, v.roll, 1 - up * 0.7, haze(sn.d) * (0.4 + 0.6 * up));
        },
      });
    }
    for (const pe of w.pelicans) {
      items.push({
        d: pe.d,
        paint: () => {
          const flap = Math.sin(t * 9 + pe.phase);
          const p = project(v, pe.x, pe.d, pe.h + 0.15 * flap);
          const px = this.bird.metres * p.s;
          const fade = pe.state === 'gone' ? Math.max(0.2, 1 - (t - pe.goneAt) / 1.5) : 1;
          // its shadow on the water, faint, tells you how high it is
          const sh = project(v, pe.x, pe.d, 0);
          ctx.globalAlpha = 0.18 * haze(pe.d);
          ctx.fillStyle = '#0b3b3f';
          ctx.beginPath();
          ctx.ellipse(sh.sx, sh.sy, Math.max(3, 0.6 * sh.s), Math.max(1.5, 0.12 * sh.s), v.roll, 0, Math.PI * 2);
          ctx.fill();
          ctx.save();
          ctx.translate(p.sx, p.sy);
          ctx.rotate(v.roll);
          ctx.scale(pe.vx < 0 ? -1 : 1, 1 - 0.25 * Math.abs(flap));
          ctx.globalAlpha = haze(pe.d) * fade;
          ctx.drawImage(this.bird.canvas, -px / 2, -this.bird.waterline * px, px, px);
          ctx.restore();
          ctx.globalAlpha = 1;
        },
      });
    }
    if (w.face) {
      const face = w.face;
      this.loadFace(face.who);
      items.push({
        d: face.d,
        paint: () => {
          const imgs = this.faceImgs[face.who];
          if (!imgs || !imgs.head.complete || !imgs.jaw.complete || !imgs.head.naturalWidth) return;
          const fr = FACES[face.who];
          const bob = 0.06 * Math.sin(t * 1.9 + face.born);
          const rise = face.state === 'gone' ? -Math.min(1, (t - face.goneAt) / 0.8) * fr.metres * 0.9 : Math.min(0, -(1 - Math.min(1, (t - face.born) / TUNING.feed.rise)) * fr.metres * 0.9);
          const p = project(v, face.x, face.d, bob + rise);
          const px = fr.metres * p.s;
          const top = -fr.waterline * px;
          ctx.save();
          ctx.translate(p.sx, p.sy);
          ctx.rotate(v.roll + 0.05 * Math.sin(t * 1.3 + face.born));
          // nothing below the waterline shows
          ctx.beginPath();
          ctx.rect(-px, top - px, 2 * px, px * (fr.waterline + 0.02) + px);
          ctx.clip();
          ctx.globalAlpha = haze(face.d);
          ctx.drawImage(imgs.head, -px / 2, top, px, px);
          ctx.drawImage(imgs.jaw, -px / 2, top + face.open * fr.jawDrop * px, px, px);
          ctx.restore();
          ctx.globalAlpha = 1;
        },
      });
    }
    for (const gr of w.grads) {
      const up = gradUp(gr, t);
      if (up <= 0) continue;
      items.push({
        d: gr.d,
        paint: () => {
          const p = project(v, gr.x, gr.d, 0.05 + 0.04 * Math.sin(t * 1.8 + gr.id));
          const px = this.grad.metres * p.s;
          this.sprite(this.grad, p.sx, p.sy, px, v.roll, 1 - up * 0.8, haze(gr.d) * (0.4 + 0.6 * up));
          if (gr.cap) {
            const cp = project(v, gr.x, gr.d, gr.cap.h);
            const cpx = Math.max(6, this.cap.metres * cp.s * 1.4);
            ctx.save();
            ctx.translate(cp.sx, cp.sy);
            ctx.rotate((t - gr.cap.t0) * 6);
            ctx.globalAlpha = haze(gr.d);
            ctx.drawImage(this.cap.canvas, -cpx / 2, -cpx / 2, cpx, cpx);
            ctx.restore();
            ctx.globalAlpha = 1;
          }
        },
      });
    }
    for (const mk of w.monkeys) {
      items.push({
        d: mk.d,
        paint: () => {
          const fade = mk.state === 'gone' ? Math.max(0, 1 - (t - mk.goneAt) / 1.2) : 1;
          const bob = 0.05 * Math.sin(t * 2.3 + mk.id);
          const p = project(v, mk.x, mk.d, bob);
          const px = this.log.metres * p.s;
          ctx.save();
          ctx.translate(p.sx, p.sy);
          ctx.rotate(v.roll + 0.04 * Math.sin(t * 1.7 + mk.id));
          ctx.globalAlpha = haze(mk.d) * fade;
          // the log, then the monkey sitting on it (paddling: a little rock back and forth)
          ctx.drawImage(this.log.canvas, -px / 2, -this.log.waterline * px, px, px);
          if (this.monkey.complete && this.monkey.naturalWidth) {
            const mh = 0.95 * p.s; // the monkey is about a metre tall, sitting
            const mw = mh * (this.monkey.naturalWidth / this.monkey.naturalHeight);
            ctx.save();
            ctx.translate(-0.1 * px, -0.17 * px); // the log's top surface
            ctx.rotate(0.12 * Math.sin(t * 4 + mk.id));
            ctx.drawImage(this.monkey, -mw / 2, -mh, mw, mh);
            ctx.restore();
          }
          ctx.restore();
          ctx.globalAlpha = 1;
          if (mk.state === 'coming' && Math.floor(t * 3) % 2 === 0) {
            ctx.font = `800 ${Math.round(Math.max(13, 0.5 * p.s))}px -apple-system, ui-rounded, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillStyle = '#ffd166';
            ctx.strokeStyle = 'rgba(0,0,0,0.5)';
            ctx.lineWidth = 3;
            ctx.strokeText('🐒', p.sx, p.sy - 0.9 * px);
            ctx.fillText('🐒', p.sx, p.sy - 0.9 * px);
          }
        },
      });
    }
    for (const sh of w.sharks) {
      items.push({
        d: sh.d,
        paint: () => {
          const p = project(v, sh.x, sh.d, 0.02);
          const px = this.shark.metres * p.s;
          const fade = sh.state === 'gone' ? Math.max(0, 1 - (t - sh.goneAt) / 0.8) : 1;
          ctx.save();
          ctx.translate(p.sx, p.sy);
          ctx.rotate(v.roll);
          // heading right by default; crossing left is mirrored; a charging fin faces us and narrows
          const facing = sh.state === 'charge' ? 0.45 : sh.vx < 0 ? -1 : 1;
          ctx.scale(facing, 1);
          ctx.globalAlpha = haze(sh.d) * fade;
          ctx.drawImage(this.shark.canvas, -px / 2, -this.shark.waterline * px, px, px);
          ctx.restore();
          if (sh.state === 'charge' && Math.floor(t * 3) % 2 === 0) {
            ctx.font = `800 ${Math.round(Math.max(14, 0.9 * p.s))}px -apple-system, ui-rounded, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillStyle = '#ff3b5c';
            ctx.strokeStyle = 'rgba(0,0,0,0.5)';
            ctx.lineWidth = 3;
            ctx.strokeText('!', p.sx, p.sy - this.shark.waterline * px - 6);
            ctx.fillText('!', p.sx, p.sy - this.shark.waterline * px - 6);
          }
        },
      });
    }
    for (const s of w.splashes) {
      items.push({
        d: s.d,
        paint: () => {
          const age = (t - s.t0) / s.life;
          const grow = Math.min(1, (t - s.t0) / 1.1);
          const p = project(v, s.x, s.d, 0);
          const r = (s.big ? 0.9 : 0.55) * p.s * (0.3 + grow);
          ctx.globalAlpha = (1 - age) * haze(s.d);
          ctx.strokeStyle = '#f4ffff';
          ctx.lineWidth = Math.max(1, 0.08 * p.s);
          ctx.beginPath();
          ctx.ellipse(p.sx, p.sy, r, r * 0.35, v.roll, 0, Math.PI * 2);
          ctx.stroke();
          if (grow < 0.5) {
            // the spout
            const hgt = (s.big ? 1.4 : 0.8) * Math.sin(grow * Math.PI * 2);
            const top = project(v, s.x, s.d, hgt);
            ctx.fillStyle = 'rgba(255,255,255,0.85)';
            ctx.beginPath();
            ctx.ellipse(top.sx, top.sy + (p.sy - top.sy) / 2, r * 0.35, (p.sy - top.sy) / 2, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        },
      });
    }
    items.sort((a, b) => b.d - a.d);
    for (const it of items) it.paint();

    // score pops float up, always on top
    for (const pop of w.pops) {
      const age = (t - pop.t0) / 1.2;
      const p = project(v, pop.x, pop.d, 0.4 + age * 1.5);
      ctx.globalAlpha = 1 - age * age;
      ctx.font = `800 ${Math.round(16 + 10 * (1 - age))}px -apple-system, ui-rounded, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.fillStyle = pop.good ? '#ffd166' : '#ff6b8a';
      ctx.strokeText(pop.text, p.sx, p.sy);
      ctx.fillText(pop.text, p.sx, p.sy);
      ctx.globalAlpha = 1;
    }
  }

  /**
   * Draw a sprite with its waterline at (sx, sy). `under` is how much of the sprite's height is
   * hidden below the water: 0 shows all of it, 1 none. Rotation is about the waterline point.
   */
  private sprite(sp: Sprite, sx: number, sy: number, px: number, rot: number, under: number, alpha: number): void {
    const { ctx } = this;
    const half = px / 2;
    const top = -sp.waterline * px;
    const cutoff = top + (1 - Math.max(0, Math.min(1, under))) * px;
    if (cutoff <= top) return;
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(rot);
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.rect(-half - 2, top - 2, px + 4, cutoff - top + 2);
    ctx.clip();
    ctx.drawImage(sp.canvas, -half, top, px, px);
    ctx.restore();
  }

  private gun(w: World, v: View, aim: Aim | null): void {
    const { ctx } = this;
    const { w: W, h: H } = v;
    const gx = this.gunX;
    const gy = this.gunY;
    const pivotY = gy - 44;

    // things in flight: shadow on the water, then the thing itself, spinning
    for (const h of w.projectiles) {
      const p = { ...project(v, h.x, h.d, Math.max(0, h.h)) };
      const sh = { ...project(v, h.x, h.d, 0) };
      const a = haze(h.d);
      ctx.globalAlpha = 0.35 * a;
      ctx.fillStyle = '#0b3b3f';
      ctx.beginPath();
      ctx.ellipse(sh.sx, sh.sy, Math.min(16, Math.max(2, 0.18 * sh.s)), Math.min(6, Math.max(1, 0.06 * sh.s)), v.roll, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = a;
      const sp = this.sprites[h.kind];
      const px = Math.max(5, sp.metres * p.s * 1.4);
      if (h.kind === 'super') {
        // the super pineapple glows
        const g = ctx.createRadialGradient(p.sx, p.sy, 0, p.sx, p.sy, px);
        g.addColorStop(0, 'rgba(255,220,90,0.7)');
        g.addColorStop(1, 'rgba(255,220,90,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, px, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.save();
      ctx.translate(p.sx, p.sy);
      ctx.rotate((w.t - h.firedAt) * (h.kind === 'shoe' ? 12 : 7));
      ctx.drawImage(sp.canvas, -px / 2, -px / 2, px, px);
      ctx.restore();
      ctx.globalAlpha = 1;
    }

    // while aiming: the bearing line on the water (left/right only, never the range)
    if (aim) {
      const phi = (aim.azimuth * Math.PI) / 180;
      ctx.save();
      ctx.setLineDash([6, 10]);
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let d = 2.5; d <= 70; d *= 1.08) {
        const p = project(v, d * Math.sin(phi), d * Math.cos(phi), 0);
        if (d === 2.5) ctx.moveTo(p.sx, p.sy);
        else ctx.lineTo(p.sx, p.sy);
      }
      ctx.stroke();
      ctx.restore();
    }

    // the thrower: a shoulder at the rail and an arm that points along the bearing, lifted by the elevation
    const az = aim ? aim.azimuth : 0;
    const el = aim ? aim.elevation : 12;
    const far = project(v, 60 * Math.sin((az * Math.PI) / 180), 60 * Math.cos((az * Math.PI) / 180), 0);
    const tx = far.sx - gx;
    const ty = far.sy - 0.45 * H * Math.sin((el * Math.PI) / 180) - pivotY;
    const tn = Math.hypot(tx, ty) || 1;
    const bl = 58 + 34 * Math.sin((el * Math.PI) / 180);
    const bx = (tx / tn) * bl;
    const by = (ty / tn) * bl;
    ctx.save();
    ctx.translate(gx, gy);
    // shoulder
    ctx.fillStyle = '#1c2429';
    ctx.beginPath();
    ctx.moveTo(-34, 0);
    ctx.quadraticCurveTo(-30, -52, 0, -54);
    ctx.quadraticCurveTo(30, -52, 34, 0);
    ctx.closePath();
    ctx.fill();
    ctx.translate(0, -44);
    // arm
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#c99a72';
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(bx, by);
    ctx.stroke();
    // the thing in hand (hidden for the pick-up moment after a throw)
    if (w.t >= w.reloadUntil - 0.12) {
      const sp = this.sprites[w.item];
      const px = 30;
      ctx.translate(bx, by);
      ctx.drawImage(sp.canvas, -px / 2, -px / 2 - 6, px, px);
    }
    ctx.restore();

    // the power meter while charging
    if (aim?.charging) {
      const mx = Math.min(W - 26, gx + 120);
      const mh = 130;
      const my = gy - 60;
      ctx.fillStyle = 'rgba(11,32,39,0.55)';
      ctx.beginPath();
      ctx.roundRect(mx - 9, my - mh - 6, 18, mh + 12, 9);
      ctx.fill();
      const g = ctx.createLinearGradient(0, my, 0, my - mh);
      g.addColorStop(0, '#06d6a0');
      g.addColorStop(0.6, '#ffd166');
      g.addColorStop(1, '#ef476f');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.roundRect(mx - 6, my - mh * aim.power, 12, mh * aim.power, 6);
      ctx.fill();
      // a white tick at the level, so the reading doesn't depend on colour
      ctx.fillStyle = '#fff';
      ctx.fillRect(mx - 14, my - mh * aim.power - 1.5, 28, 3);
      ctx.font = '700 15px -apple-system, ui-rounded, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 3;
      const label = `${Math.round(aim.power * 100)}%  ·  ${Math.round(aim.elevation)}°  ${aim.azimuth >= 0 ? '→' : '←'}${Math.abs(Math.round(aim.azimuth))}°`;
      ctx.strokeText(label, mx, my - mh - 16);
      ctx.fillText(label, mx, my - mh - 16);
    }

    // the rail along the bottom edge (above any system bar), hull colour below it
    ctx.fillStyle = '#17323a';
    ctx.fillRect(-10, gy - 10, W + 20, H - gy + 20);
    ctx.fillStyle = 'rgba(236,240,242,0.9)';
    ctx.fillRect(-10, gy - 26, W + 20, 8);
    ctx.fillStyle = 'rgba(180,190,196,0.9)';
    ctx.fillRect(-10, gy - 18, W + 20, 3);
  }
}

/** atmospheric perspective: far things fade toward the haze */
function haze(d: number): number {
  return 1 - 0.45 * Math.max(0, Math.min(1, (d - 15) / 70));
}
